import { useMemo, useState } from "react";
import { closeModal, Modal, TextInput } from "risa-oasis-ui_v2";
import MultiSelectTagInput from "../../../../../components/multiSelectTagInut/MultiSelectTagInput";
import { ModalId } from "../../../../../enums/modalId";
import type { PocGroupFormData } from "../data-model/pocAssignmentModel";

interface AddGroupModalProps {
  /** Distinct batch values available for selection. */
  batchOptions: string[];
  /** True while filename options are loading from the condition list. */
  batchOptionsLoading?: boolean;
  /** Returns true when the group name already exists. */
  hasGroup: (groupName: string) => boolean;
  /** Persists the new group (e.g. to Firestore); modal closes only after this resolves. */
  onSave: (data: PocGroupFormData) => void | Promise<void>;
}

const EMPTY_FORM: PocGroupFormData = {
  groupName: "",
  batches: [],
};

/**
 * Modal for adding a new group with optional default batches.
 * Opened imperatively with `openModal(ModalId.ADD_GROUP_MODAL)`.
 */
const AddGroupModal = ({
  batchOptions,
  batchOptionsLoading = false,
  hasGroup,
  onSave,
}: AddGroupModalProps) => {
  const [form, setForm] = useState<PocGroupFormData>(EMPTY_FORM);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const batchSelectOptions = useMemo(
    () =>
      batchOptions.map((batch) => ({
        value: batch,
        label: batch,
      })),
    [batchOptions],
  );

  const selectedBatchOptions = useMemo(
    () =>
      form.batches.map((batch) => {
        const fromList = batchSelectOptions.find((o) => o.value === batch);
        return fromList ?? { value: batch, label: batch };
      }),
    [batchSelectOptions, form.batches],
  );

  const close = () => {
    setForm(EMPTY_FORM);
    setError("");
    closeModal(ModalId.ADD_GROUP_MODAL);
  };

  const handleSave = async () => {
    const groupName = form.groupName.trim();
    if (!groupName) {
      setError("Group name is required");
      return;
    }
    if (hasGroup(groupName)) {
      setError("A group with this name already exists");
      return;
    }

    setIsSaving(true);
    setError("");
    try {
      await onSave({ groupName, batches: form.batches });
      close();
    } catch {
      setError("Save failed. See toast for details.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      dialogId={ModalId.ADD_GROUP_MODAL}
      title="Add"
      onClose={close}
      onCancel={close}
      onSave={handleSave}
      saveButtonText={isSaving ? "Saving…" : "Save"}
      cancelText="Cancel"
      disableSave={!form.groupName.trim() || isSaving}
      heightPercentage={70}
    >
      <div className="flex flex-col gap-4 py-2">
        <TextInput
          id="add-group-name"
          label="Group Name"
          placeholder="e.g. G4"
          defaultValue={form.groupName}
          error={error}
          onChange={(event) => {
            setError("");
            setForm((prev) => ({ ...prev, groupName: event.value }));
          }}
        />
        <div className="flex flex-col gap-1">
          <span className="text-tiny font-medium text-primaryGray-8">
            Batches
          </span>
          <MultiSelectTagInput
            id="add-group-batches"
            options={batchSelectOptions}
            selectedOptions={selectedBatchOptions}
            placeholder={
              batchOptionsLoading ? "Loading batches…" : "Select Batches Like"
            }
            onChange={(values) =>
              setForm((prev) => ({ ...prev, batches: values }))
            }
          />
        </div>
      </div>
    </Modal>
  );
};

export default AddGroupModal;
